export interface KnowledgeBase {
  id: string;
  name: string;
  description: string;
  coverImage: string;
  tags: string[];
  updatedAt: string;
  size: string;
  fileCount: number;
  memberCount: number;
  itemCount?: number;
  /** 当前用户与该库的关系，用于「我创建的 / 我加入的」筛选 */
  relation?: "created" | "joined";
}

export interface KnowledgeItem {
  id: string;
  kbId: string;
  sourceType: 'pdf' | 'docx' | 'doi' | 'ris' | 'arxiv' | 'chat' | 'social' | 'code' | 'result';
  category: 'literature' | 'document' | 'chat' | 'data' | 'code' | 'result';
  title: string;
  description: string;
  tags: string[];
  updatedAt: string;
  size?: string;
  verified?: boolean;
  author?: string;
  journal?: string;
  year?: string;
  // Literature metadata (optional, for arXiv/DOI/PDF)
  abstract?: string;
  authors?: string[];
  arxivId?: string;
  absUrl?: string;
  pdfUrl?: string;
}

export const knowledgeBases: KnowledgeBase[] = [
  {
    id: "kb1",
    name: "产品设计规范",
    description: "包含 UI/UX 设计原则、组件规范与交互指南",
    coverImage: "https://readdy.ai/api/search-image?query=abstract%20purple%20violet%20gradient%20wave%20minimal%20UI%20design%20system%20component%20library%20elegant%20smooth%20background%20professional%20modern%20aesthetic&width=600&height=340&seq=kb001&orientation=landscape",
    tags: ["设计", "UI"],
    updatedAt: "2026-04-10 15:00",
    size: "32.45MB",
    fileCount: 43,
    memberCount: 71,
    relation: "created",
  },
  {
    id: "kb2",
    name: "技术架构文档",
    description: "系统架构设计、接口规范及技术选型说明",
    coverImage: "https://readdy.ai/api/search-image?query=green%20teal%20glowing%20circuit%20board%20technology%20digital%20abstract%20software%20system%20architecture%20infrastructure%20network%20pattern%20dark%20background%20professional&width=600&height=340&seq=kb002&orientation=landscape",
    tags: ["技术", "架构"],
    updatedAt: "2026-04-08 09:30",
    size: "18.20MB",
    fileCount: 43,
    memberCount: 5,
    relation: "joined",
  },
  {
    id: "kb3",
    name: "市场调研报告",
    description: "2026年Q1产品分析与用户调查研究",
    coverImage: "https://readdy.ai/api/search-image?query=orange%20bar%20chart%20data%20visualization%20world%20map%20global%20research%20analytics%20business%20intelligence%20professional%20dark%20blue%20background%20glowing%20statistics%20infographic&width=600&height=340&seq=kb003&orientation=landscape",
    tags: ["市场", "调研"],
    updatedAt: "2026-04-05 17:45",
    size: "56.80MB",
    fileCount: 28,
    memberCount: 12,
    relation: "created",
  },
  {
    id: "kb4",
    name: "用户增长策略",
    description: "用户获取、激活与留存的完整方法论",
    coverImage: "https://readdy.ai/api/search-image?query=orange%20arrow%20growth%20upward%20financial%20success%20achievement%20strategy%20chart%20minimal%20clean%20abstract%20background%20warm%20golden%20gradient%20professional%20modern&width=600&height=340&seq=kb004&orientation=landscape",
    tags: ["增长", "运营"],
    updatedAt: "2026-04-01 11:20",
    size: "9.60MB",
    fileCount: 19,
    memberCount: 3,
    relation: "joined",
  },
  {
    id: "kb5",
    name: "团队协作手册",
    description: "工作流程、沟通规范与项目管理最佳实践",
    coverImage: "https://readdy.ai/api/search-image?query=diverse%20people%20teamwork%20collaboration%20office%20work%20professional%20business%20team%20illustration%20soft%20light%20blue%20warm%20background%20minimal%20clean%20modern&width=600&height=340&seq=kb005&orientation=landscape",
    tags: ["协作", "管理"],
    updatedAt: "2026-03-28 14:00",
    size: "7.30MB",
    fileCount: 35,
    memberCount: 8,
    relation: "joined",
  },
  {
    id: "kb6",
    name: "AI 产品案例集",
    description: "精选全球优秀 AI 产品设计案例与分析",
    coverImage: "https://readdy.ai/api/search-image?query=purple%20violet%20bokeh%20glowing%20light%20particles%20artificial%20intelligence%20technology%20abstract%20dark%20moody%20background%20dreamy%20neural%20network%20pattern%20aesthetic&width=600&height=340&seq=kb006&orientation=landscape",
    tags: ["AI", "案例"],
    updatedAt: "2026-03-20 10:15",
    size: "124.50MB",
    fileCount: 67,
    memberCount: 24,
    relation: "created",
  },
];

export const knowledgeItems: KnowledgeItem[] = [
  {
    id: "doc1",
    kbId: "kb1",
    sourceType: "pdf",
    category: "document",
    title: "UI组件设计规范 v3.2",
    description: "涵盖按钮、表单、导航、卡片等核心组件的设计原则与使用规范，含标注与代码示例。",
    tags: ["组件库", "设计规范"],
    updatedAt: "2026-04-10 15:00",
    size: "4.2MB",
    verified: true,
    author: "张晓明",
  },
  {
    id: "doc2",
    kbId: "kb1",
    sourceType: "docx",
    category: "document",
    title: "交互设计原则与案例分析",
    description: "从认知心理学角度阐述交互设计的核心原则，结合产品实际案例深度分析。",
    tags: ["交互设计", "UX"],
    updatedAt: "2026-04-09 11:30",
    size: "2.8MB",
    verified: false,
    author: "李思远",
  },
  {
    id: "doc3",
    kbId: "kb1",
    sourceType: "chat",
    category: "chat",
    title: "对话：颜色系统与无障碍设计",
    description: "关于色彩对比度、色盲友好方案和 WCAG 2.2 标准的深度对话记录与关键结论。",
    tags: ["颜色系统", "无障碍"],
    updatedAt: "2026-04-08 16:20",
    verified: false,
  },
  {
    id: "doc4",
    kbId: "kb2",
    sourceType: "docx",
    category: "document",
    title: "微服务架构设计文档 v2.0",
    description: "系统整体架构说明，包含服务拆分策略、通信协议、数据一致性方案与部署规范。",
    tags: ["微服务", "架构设计"],
    updatedAt: "2026-04-08 09:30",
    size: "6.1MB",
    verified: true,
    author: "王工",
  },
  {
    id: "doc5",
    kbId: "kb2",
    sourceType: "pdf",
    category: "document",
    title: "API 接口设计规范",
    description: "RESTful API 设计标准、错误码规范、版本管理策略与接口文档示例。",
    tags: ["API", "接口规范"],
    updatedAt: "2026-04-07 14:15",
    size: "1.9MB",
    verified: true,
  },
  {
    id: "doc6",
    kbId: "kb3",
    sourceType: "pdf",
    category: "result",
    title: "Q1 用户调研报告（N=2847）",
    description: "2026年第一季度用户深度访谈与问卷调研综合报告，含用户画像、痛点与需求优先级。",
    tags: ["用户研究", "调研报告"],
    updatedAt: "2026-04-05 17:45",
    size: "12.3MB",
    verified: true,
    author: "陈研究员",
  },
];

export const knowledgeStats = {
  total: 6,
  personal: 2,
  team: 4,
};
